import { useState, useEffect, useContext, useMemo } from "react";
import toast from "../../utils/toast";
import { useParams, useHistory } from "react-router-dom";
import { blogsApi, categoriesApi, uploadApi } from "../../services";
import { Context } from "../../Context/Context";
import TipTapEditor from "./TipTapEditor";
import "./blogForm.css";

// Client-side slugify (mirrors backend logic)
function slugifyText(text) {
  if (!text) return '';
  return text
    .normalize('NFD')
    .replace(/đ/g, 'd').replace(/Đ/g, 'd')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// Categories that belong to hoanglecf.com (community/news).
// All other categories go to thueacccf.net (blog satellite).
const HOANGLECF_CATEGORIES = ['cong-dong', 'hong-bien'];

function getSiteForCategory(cat) {
  if (!cat) return 'thueacccf';
  const slug = cat.slug || cat.name?.toLowerCase().replace(/\s+/g, '-') || '';
  return HOANGLECF_CATEGORIES.some(s => slug.includes(s)) ? 'hoanglecf' : 'thueacccf';
}

// hoanglecf.com và hoanglecf.vn dùng chung bộ danh mục (cong-dong, hong-bien),
// chỉ khác nhau ở displayOn để hiển thị bài viết riêng cho từng domain.
const HOANGLECF_FAMILY_SITES = ['hoanglecf', 'hoanglecf-vn'];

// Convert legacy Editor.js block data into HTML so old posts can be edited in
// the new rich-text editor (which works with HTML). New posts are already HTML.
function editorJsToHtml(data) {
  if (!data || !Array.isArray(data.blocks)) return "";
  const listToHtml = (items, style) => {
    if (!Array.isArray(items)) return "";
    const tag = style === "ordered" ? "ol" : "ul";
    const inner = items
      .map((it) => {
        const content = it?.content ?? it?.text ?? "";
        const nested = Array.isArray(it?.items) && it.items.length ? listToHtml(it.items, style) : "";
        return `<li>${content}${nested}</li>`;
      })
      .join("");
    return `<${tag}>${inner}</${tag}>`;
  };
  return data.blocks
    .map((block) => {
      const d = block?.data || {};
      switch (block?.type) {
        case "paragraph":
          return `<p>${d.text || ""}</p>`;
        case "header": {
          const lvl = Math.min(Math.max(d.level || 2, 1), 6);
          return `<h${lvl}>${d.text || ""}</h${lvl}>`;
        }
        case "list":
          return listToHtml(d.items, d.style);
        case "checklist":
          return `<ul>${(d.items || []).map((i) => `<li>${i?.checked ? "☑" : "☐"} ${i?.text || ""}</li>`).join("")}</ul>`;
        case "quote":
          return `<blockquote>${d.text || ""}${d.caption ? `<br><cite>${d.caption}</cite>` : ""}</blockquote>`;
        case "image": {
          const url = d.file?.url || d.url;
          if (!url) return "";
          return `<p><img src="${url}" alt="${d.caption || ""}"></p>${d.caption ? `<p><em>${d.caption}</em></p>` : ""}`;
        }
        case "embed":
          return d.embed ? `<p><iframe class="ql-video" src="${d.embed}" frameborder="0" allowfullscreen></iframe></p>` : "";
        case "raw":
          return d.html || "";
        case "linkTool": {
          const href = d.link || d.meta?.url;
          return href ? `<p><a href="${href}">${d.meta?.title || href}</a></p>` : "";
        }
        default:
          return "";
      }
    })
    .join("");
}

// Stored content may be legacy Editor.js JSON or plain HTML; normalize to HTML.
function normalizeContentToHtml(raw) {
  if (!raw) return "";
  let data = null;
  try {
    data = JSON.parse(raw);
  } catch (_) {
    data = null;
  }
  if (data && Array.isArray(data.blocks)) return editorJsToHtml(data);
  return raw;
}

export default function BlogForm() {
  const { id } = useParams();
  const history = useHistory();
  const { user } = useContext(Context);
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    metaTitle: "",
    metaDescription: "",
    tags: [],
    displayOn: ["hoanglecf"],
    content: "",
    authorId: user?._id || "",
    category: {
      _id: "",
      name: "",
      slug: ""
    },
    coverImage: "",
    images: [],
    videos: [],
    status: "draft"
  });

  const [slugManual, setSlugManual] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [showSeoSection, setShowSeoSection] = useState(false);

  const [categories, setCategories] = useState([]);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [imageFiles] = useState([]);
  const [videoFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [showPreview, setShowPreview] = useState(false);
  const [livePreview, setLivePreview] = useState(false);
  const [content, setContent] = useState("");

  // Memoize the cover-image blob preview so it isn't recreated on every render
  // (otherwise the <img> re-fetches continuously and leaks object URLs).
  const coverPreview = useMemo(
    () => (coverImageFile ? URL.createObjectURL(coverImageFile) : null),
    [coverImageFile]
  );
  useEffect(() => {
    return () => { if (coverPreview) URL.revokeObjectURL(coverPreview); };
  }, [coverPreview]);

  // Blob previews for newly-added (not-yet-uploaded) images/videos, used by the
  // "Xem trước" overview. Memoized + revoked so we don't leak object URLs.
  const newImagePreviews = useMemo(
    () => imageFiles.map((f) => URL.createObjectURL(f)),
    [imageFiles]
  );
  useEffect(() => {
    return () => { newImagePreviews.forEach((u) => URL.revokeObjectURL(u)); };
  }, [newImagePreviews]);

  const newVideoPreviews = useMemo(
    () => videoFiles.map((f) => URL.createObjectURL(f)),
    [videoFiles]
  );
  useEffect(() => {
    return () => { newVideoPreviews.forEach((u) => URL.revokeObjectURL(u)); };
  }, [newVideoPreviews]);

  // const PF = "http://localhost:5000";
  const PF = "https://backend.hoanglecf.com";

  useEffect(() => {
    fetchCategories();
    if (isEdit) {
      fetchBlogData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);



  const fetchCategories = async () => {
    try {
      const response = await categoriesApi.getAllCategories();
      setCategories(response);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchBlogData = async () => {
    try {
      setLoadingData(true);
      const blog = await blogsApi.getBlogById(id);
      setFormData({
        title: blog.title || "",
        slug: blog.slug || "",
        excerpt: blog.excerpt || "",
        metaTitle: blog.metaTitle || "",
        metaDescription: blog.metaDescription || "",
        tags: blog.tags || [],
        displayOn: blog.displayOn || ["hoanglecf"],
        content: blog.content || "",
        authorId: blog.authorId?._id || user._id,
        category: blog.category || { _id: "", name: "", slug: "" },
        coverImage: blog.coverImage || "",
        images: blog.images || [],
        videos: blog.videos || [],
        status: blog.status || "draft"
      });
      if (blog.slug) setSlugManual(true);
      // Legacy posts are stored as Editor.js JSON — convert to HTML for the editor.
      setContent(normalizeContentToHtml(blog.content || ""));
    } catch (error) {
      console.error("Error fetching blog:", error);
      toast.error("Không thể tải dữ liệu blog!");
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: type === "checkbox" ? checked : value };
      // Auto-generate slug khi title thay đổi (nếu chưa set thủ công)
      if (name === 'title' && !slugManual) {
        updated.slug = slugifyText(value);
      }
      return updated;
    });
  };

  const handleSlugChange = (e) => {
    setSlugManual(true);
    setFormData(prev => ({ ...prev, slug: slugifyText(e.target.value) }));
  };

  const handleTagKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      const tag = tagInput.trim().toLowerCase();
      if (!formData.tags.includes(tag)) {
        setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      }
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    const selectedCategory = categories.find(cat => cat._id === categoryId);
    
    if (selectedCategory) {
      const catObj = {
        _id: selectedCategory._id,
        name: selectedCategory.name,
        slug: selectedCategory.slug || selectedCategory.name.toLowerCase().replace(/\s+/g, '-')
      };
      const site = getSiteForCategory(catObj);
      setFormData(prev => ({
        ...prev,
        category: catObj,
        displayOn: [site]
      }));
    }
  };

  const handleSiteChange = (site) => {
    setFormData(prev => {
      const updated = { ...prev, displayOn: [site] };
      // Clear category if it doesn't belong to the new site
      if (prev.category?._id) {
        const catSite = getSiteForCategory(prev.category);
        if (catSite !== site) {
          updated.category = { _id: '', name: '', slug: '' };
        }
      }
      return updated;
    });
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB limit for images)
      if (file.size > 10 * 1024 * 1024) {
        toast.warning("Ảnh đại diện không được vượt quá 10MB!");
        e.target.value = ''; // Clear the input
        return;
      }
      setCoverImageFile(file);
    }
  };



  const handlePreview = () => {
    setShowPreview(true);
  };

  const previewCoverSrc = coverImageFile
    ? coverPreview
    : formData.coverImage
      ? PF + formData.coverImage
      : null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.warning("Vui lòng nhập tiêu đề blog!");
      return;
    }

    // Content comes from the rich-text editor as HTML. Treat it as empty if it
    // has no text and no media (images/iframes/embeds).
    const hasText = content.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;
    const hasMedia = /<(img|iframe|video|figure)[\s>]/i.test(content);
    if (!hasText && !hasMedia) {
      toast.warning("Vui lòng nhập nội dung blog!");
      return;
    }

    if (!formData.category._id) {
      toast.warning("Vui lòng chọn danh mục!");
      return;
    }

    try {
      setLoading(true);
      
      let coverImageUrl = formData.coverImage;
      
      // Upload cover image if new file is selected
      if (coverImageFile) {
        const filename = Date.now() + coverImageFile.name;
        const uploadResult = await uploadApi.uploadImage(coverImageFile, filename);
        coverImageUrl = uploadResult.path;
      }

      // Upload new images
      const newImageUrls = [];
      for (const file of imageFiles) {
        const filename = Date.now() + Math.random() + file.name;
        const uploadResult = await uploadApi.uploadImage(file, filename);
        newImageUrls.push(uploadResult.path);
      }

      // Upload new videos
      const newVideoUrls = [];
      for (const file of videoFiles) {
        const filename = Date.now() + Math.random() + file.name;
        const uploadResult = await uploadApi.uploadVideo(file, filename);
        newVideoUrls.push(uploadResult.path);
      }

      const blogData = {
        ...formData,
        content,
        coverImage: coverImageUrl,
        images: [...formData.images, ...newImageUrls],
        videos: [...formData.videos, ...newVideoUrls]
      };

      // Nếu slug set thủ công, đánh dấu cho backend
      if (slugManual && blogData.slug) {
        blogData._slugManuallySet = true;
      }

      if (isEdit) {
        await blogsApi.updateBlog(id, blogData);
        toast.success("Cập nhật blog thành công!");
      } else {
        await blogsApi.createBlog(blogData);
        toast.success("Tạo blog thành công!");
      }
      
      history.push("/blogs");
    } catch (error) {
      console.error("Error saving blog:", error);
      toast.error("Có lỗi xảy ra khi lưu blog!");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.push("/blogs");
  };

  if (loadingData) {
    return (
      <div className="blog-form-page">
        <div className="blog-form-container">
          <div className="loading">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Đang tải...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-form-page">
      <div className="blog-form-container">
        <div className="blog-form-header">
          <h1>{isEdit ? "Chỉnh sửa blog" : "Tạo blog mới"}</h1>
          <p>{isEdit ? "Cập nhật thông tin blog" : "Điền thông tin để tạo blog mới"}</p>
        </div>

        <form className="blog-form" onSubmit={handleSubmit}>
          {/* Title */}
          <div className="form-section">
            <label className="form-label">Tiêu đề blog <span className="required">*</span></label>
            <div className="input-group">
              <i className="fas fa-heading input-icon"></i>
              <input
                className="form-input"
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Nhập tiêu đề blog"
                required
              />
            </div>
          </div>

          {/* Website (chọn trước danh mục) */}
          <div className="form-section">
            <label className="form-label">Website <span className="required">*</span></label>
            <div className="display-on-options">
              <label className={`display-on-option ${formData.displayOn?.includes('hoanglecf') ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="displayOn"
                  checked={formData.displayOn?.includes('hoanglecf')}
                  onChange={() => handleSiteChange('hoanglecf')}
                />
                <i className="fas fa-globe"></i>
                <span>hoanglecf.com</span>
                <span className="site-hint">Cộng đồng, Hóng biến</span>
              </label>
              <label className={`display-on-option ${formData.displayOn?.includes('hoanglecf-vn') ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="displayOn"
                  checked={formData.displayOn?.includes('hoanglecf-vn')}
                  onChange={() => handleSiteChange('hoanglecf-vn')}
                />
                <i className="fas fa-globe"></i>
                <span>hoanglecf.vn</span>
                <span className="site-hint">Cộng đồng, Hóng biến (nội dung riêng cho .vn)</span>
              </label>
              <label className={`display-on-option ${formData.displayOn?.includes('thueacccf') ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="displayOn"
                  checked={formData.displayOn?.includes('thueacccf')}
                  onChange={() => handleSiteChange('thueacccf')}
                />
                <i className="fas fa-globe"></i>
                <span>thueacccf.net</span>
                <span className="site-hint">Thuê acc, Review, Hướng dẫn, ...</span>
              </label>
            </div>
          </div>

          {/* Category (lọc theo website) */}
          <div className="form-section">
            <label className="form-label">Danh mục <span className="required">*</span></label>
            <div className="category-options">
              {categories.map((category) => {
                const catSlug = category.slug || category.name?.toLowerCase().replace(/\s+/g, '-') || '';
                const catSite = HOANGLECF_CATEGORIES.some(s => catSlug.includes(s)) ? 'hoanglecf' : 'thueacccf';
                const currentSite = formData.displayOn?.[0] || 'hoanglecf';
                const currentSiteBucket = HOANGLECF_FAMILY_SITES.includes(currentSite) ? 'hoanglecf' : currentSite;
                const isDisabled = catSite !== currentSiteBucket;
                return (
                  <label
                    key={category._id}
                    className={`category-option ${formData.category._id === category._id ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    style={{ '--category-color': category.color || '#667eea' }}
                  >
                    <input
                      type="radio"
                      name="category"
                      value={category._id}
                      checked={formData.category._id === category._id}
                      onChange={handleCategoryChange}
                      disabled={isDisabled}
                    />
                    <div className="category-content">
                      <div className="category-icon">
                        <i className={category.icon || 'fas fa-tag'}></i>
                      </div>
                      <div className="category-info">
                        <span className="category-name">{category.name}</span>
                        {category.description && (
                          <span className="category-description">{category.description}</span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Slug */}
          <div className="form-section">
            <label className="form-label">Slug (URL)</label>
            <div className="input-group">
              <i className="fas fa-link input-icon"></i>
              <input
                className="form-input"
                type="text"
                value={formData.slug}
                onChange={handleSlugChange}
                placeholder="auto-generated-from-title"
              />
              {slugManual && (
                <button
                  type="button"
                  className="slug-reset-btn"
                  onClick={() => { setSlugManual(false); setFormData(prev => ({ ...prev, slug: slugifyText(prev.title) })); }}
                  title="Reset về tự động"
                >
                  <i className="fas fa-undo"></i>
                </button>
              )}
            </div>
            {formData.slug && (
              <span className="form-help slug-preview">
                <i className="fas fa-external-link-alt"></i> hoanglecf.com/blog/<strong>{formData.slug}</strong>
              </span>
            )}
          </div>

          {/* Excerpt */}
          <div className="form-section">
            <label className="form-label">Mô tả ngắn (Excerpt)</label>
            <textarea
              className="form-textarea"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleInputChange}
              placeholder="Tóm tắt nội dung bài viết (tối đa 300 ký tự)..."
              maxLength={300}
              rows={3}
            />
            <span className="form-help">{formData.excerpt.length}/300 ký tự</span>
          </div>

          {/* Tags */}
          <div className="form-section">
            <label className="form-label">Tags</label>
            <div className="tags-input-wrapper">
              {formData.tags.map((tag, i) => (
                <span key={i} className="tag-chip">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className="tag-remove">×</button>
                </span>
              ))}
              <input
                className="tag-input"
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Nhập tag rồi nhấn Enter..."
              />
            </div>
            <span className="form-help">Nhấn Enter hoặc dấu phẩy để thêm tag</span>
          </div>


          {/* SEO Section (Collapsible) */}
          <div className="form-section seo-section">
            <button
              type="button"
              className="seo-toggle"
              onClick={() => setShowSeoSection(v => !v)}
            >
              <i className={`fas fa-chevron-${showSeoSection ? 'up' : 'down'}`}></i>
              <i className="fas fa-search"></i>
              <span>Tùy chỉnh SEO</span>
            </button>
            {showSeoSection && (
              <div className="seo-fields">
                <div className="seo-field">
                  <label className="form-label">Meta Title</label>
                  <input
                    className="form-input"
                    type="text"
                    name="metaTitle"
                    value={formData.metaTitle}
                    onChange={handleInputChange}
                    placeholder={formData.title || "Tự động từ tiêu đề"}
                  />
                </div>
                <div className="seo-field">
                  <label className="form-label">Meta Description</label>
                  <textarea
                    className="form-textarea"
                    name="metaDescription"
                    value={formData.metaDescription}
                    onChange={handleInputChange}
                    placeholder={formData.excerpt || "Tự động từ mô tả ngắn"}
                    maxLength={200}
                    rows={2}
                  />
                  <span className="form-help">{(formData.metaDescription || '').length}/200</span>
                </div>
                {/* Google SERP Preview */}
                <div className="serp-preview">
                  <div className="serp-title">{formData.metaTitle || formData.title || "Tiêu đề bài viết"} | hoanglecf.com</div>
                  <div className="serp-url">
                    {(() => {
                      const catName = formData.category?.name?.toLowerCase() || '';
                      const isHotNews = catName.includes('hotnews') || catName.includes('hóng biến') || catName.includes('hot news');
                      const folder = isHotNews ? 'hong-bien' : 'cong-dong';
                      return `hoanglecf.com/${folder}/${formData.slug || "slug"}`;
                    })()}
                  </div>
                  <div className="serp-desc">{formData.metaDescription || formData.excerpt || "Mô tả bài viết sẽ hiển thị ở đây..."}</div>
                </div>
              </div>
            )}
          </div>

          {/* Cover Image */}
          <div className="form-section">
            <label className="form-label">Ảnh đại diện</label>
            <div className="image-upload-area">
              {coverImageFile ? (
                <div className="image-preview">
                  <img src={coverPreview} alt="Preview" />
                  <button 
                    type="button" 
                    className="remove-image"
                    onClick={() => setCoverImageFile(null)}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : formData.coverImage ? (
                <div className="image-preview">
                  <img src={PF + formData.coverImage} alt="Current" />
                  <button 
                    type="button" 
                    className="remove-image"
                    onClick={() => setFormData(prev => ({ ...prev, coverImage: "" }))}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : (
                <label htmlFor="coverImageInput" className="upload-button">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>Chọn ảnh đại diện</span>
                  <small>JPG, PNG, GIF (tối đa 10MB)</small>
                </label>
              )}
              <input
                id="coverImageInput"
                type="file"
                style={{ display: "none" }}
                onChange={handleCoverImageChange}
                accept="image/*"
              />
            </div>
          </div>

          {/* Content */}
          <div className="form-section">
            <div className="content-label-row">
              <label className="form-label">Nội dung blog <span className="required">*</span></label>
              <button
                type="button"
                className={`live-toggle ${livePreview ? "active" : ""}`}
                onClick={() => setLivePreview((v) => !v)}
                title="Hiển thị khung xem trước cập nhật trực tiếp khi soạn"
              >
                <i className="fas fa-columns"></i>
                <span>{livePreview ? "Tắt xem trực tiếp" : "Xem trực tiếp"}</span>
              </button>
            </div>

            <div className={`editor-area ${livePreview ? "split" : ""}`}>
              <div className="tiptap-editor-wrap">
                <TipTapEditor
                  content={content}
                  onChange={setContent}
                />
              </div>

              {livePreview && (
                <div className="live-preview-pane">
                  <div className="live-preview-head">
                    <i className="fas fa-eye"></i> Xem trực tiếp
                  </div>
                  <div className="live-preview-scroll">
                    <div
                      className="ql-editor live-preview-body"
                      dangerouslySetInnerHTML={{ __html: content || "<p><em>(Chưa có nội dung)</em></p>" }}
                    />
                  </div>
                </div>
              )}
            </div>

            <span className="form-help">
              Dùng thanh công cụ để định dạng. Nút ảnh sẽ tải ảnh lên máy chủ; nút video cho phép dán link YouTube/Vimeo.
            </span>
          </div>

          {/* Status */}
          <div className="form-section">
            <label className="form-label">Trạng thái</label>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="status"
                  value="draft"
                  checked={formData.status === "draft"}
                  onChange={handleInputChange}
                />
                <span className="radio-custom"></span>
                <span className="radio-label">Bản nháp</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="status"
                  value="published"
                  checked={formData.status === "published"}
                  onChange={handleInputChange}
                />
                <span className="radio-custom"></span>
                <span className="radio-label">Đã Đăng</span>
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="blog-form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={loading}
            >
              <i className="fas fa-times"></i>
              <span>Hủy</span>
            </button>
            <button
              type="button"
              className="preview-btn"
              onClick={handlePreview}
              disabled={loading}
            >
              <i className="fas fa-eye"></i>
              <span>Xem trước</span>
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Đang lưu...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  <span>{isEdit ? "Cập nhật" : "Tạo blog"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Preview overlay — mirrors the public blog article */}
      {showPreview && (
        <div className="blog-preview-overlay" onClick={() => setShowPreview(false)}>
          <div className="blog-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="blog-preview-bar">
              <span><i className="fas fa-eye"></i> Xem trước bài viết</span>
              <button
                type="button"
                className="blog-preview-close"
                onClick={() => setShowPreview(false)}
                aria-label="Đóng xem trước"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <article className="blog-preview-article">
              {previewCoverSrc && (
                <div className="bp-cover">
                  <img src={previewCoverSrc} alt={formData.title || "cover"} />
                </div>
              )}

              <h1 className="bp-title">{formData.title || "(Chưa có tiêu đề)"}</h1>

              <div className="bp-meta">
                <span className="bp-author">
                  <i className="fas fa-user"></i> Đăng bởi {user?.username || "Admin"}
                </span>
                {formData.category?.name && (
                  <span className="bp-category">{formData.category.name.toUpperCase()}</span>
                )}
                <span className={`bp-status bp-status--${formData.status}`}>
                  {formData.status === "published" ? "Đã đăng" : "Bản nháp"}
                </span>
              </div>

              <div
                className="bp-content tiptap-content"
                dangerouslySetInnerHTML={{ __html: content || "<p><em>(Chưa có nội dung)</em></p>" }}
              />


              {(formData.images.length > 0 || newImagePreviews.length > 0) && (
                <div className="bp-section">
                  <h3 className="bp-section-title"><i className="fas fa-images"></i> Hình ảnh</h3>
                  <div className="bp-images-grid">
                    {formData.images.map((img, i) => (
                      <img key={`ex-${i}`} src={PF + img} alt={`img-${i}`} />
                    ))}
                    {newImagePreviews.map((src, i) => (
                      <img key={`new-${i}`} src={src} alt={`new-img-${i}`} />
                    ))}
                  </div>
                </div>
              )}

              {(formData.videos.length > 0 || newVideoPreviews.length > 0) && (
                <div className="bp-section">
                  <h3 className="bp-section-title"><i className="fas fa-video"></i> Video</h3>
                  <div className="bp-videos-grid">
                    {formData.videos.map((v, i) => (
                      <video key={`exv-${i}`} controls>
                        <source src={PF + v} type="video/mp4" />
                      </video>
                    ))}
                    {newVideoPreviews.map((src, i) => (
                      <video key={`newv-${i}`} controls>
                        <source src={src} type="video/mp4" />
                      </video>
                    ))}
                  </div>
                </div>
              )}
            </article>
          </div>
        </div>
      )}
    </div>
  );
}
